<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';
import LaisCopyButton from 'dashboard/components-next/LaisCopyButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

const props = defineProps({
  groups: { type: Array, default: () => [] },
  dense: { type: Boolean, default: false },
  collapsibleFrom: { type: Number, default: 2 },
});

const collapsed = ref({});

const preparedGroups = computed(() =>
  props.groups.filter(group => group?.fields?.length)
);

const isCollapsed = group => {
  const index = preparedGroups.value.findIndex(item => item.id === group.id);
  const stored = collapsed.value[group.id];
  if (stored !== undefined) return stored;
  return index >= props.collapsibleFrom && group.fields.length > 8;
};

const toggleGroup = group => {
  collapsed.value = {
    ...collapsed.value,
    [group.id]: !isCollapsed(group),
  };
};

const fieldSpanClass = field =>
  field.type === 'json' || (field.text || '').length > 120
    ? 'sm:col-span-2 xl:col-span-3'
    : '';
</script>

<template>
  <div class="flex flex-col gap-4" data-test="data-registry-field-grid">
    <section
      v-for="group in preparedGroups"
      :key="group.id"
      class="overflow-hidden rounded-lais-sm border border-lais-border-soft bg-white"
      :data-group="group.id"
    >
      <header
        class="flex items-center justify-between gap-3 border-b border-lais-border-soft bg-lais-bg-soft px-4 py-2"
      >
        <h3
          class="flex items-center gap-2 font-redHatDisplay text-sm font-semibold text-lais-text"
        >
          <span
            v-if="group.icon"
            class="size-4 text-lais-text-muted"
            :class="group.icon"
            aria-hidden="true"
          />
          {{ group.title }}
          <span class="text-xs font-normal text-lais-text-muted">
            {{ group.fields.length }}
          </span>
        </h3>
        <button
          type="button"
          class="text-xs font-semibold text-lais-primary"
          :aria-expanded="!isCollapsed(group)"
          @click="toggleGroup(group)"
        >
          {{ isCollapsed(group) ? 'Expandir' : 'Recolher' }}
        </button>
      </header>

      <dl
        v-if="!isCollapsed(group)"
        class="grid gap-x-4 gap-y-3 p-4 sm:grid-cols-2 xl:grid-cols-3"
        :class="dense ? 'text-xs' : 'text-sm'"
      >
        <div
          v-for="field in group.fields"
          :key="field.key"
          class="min-w-0"
          :class="fieldSpanClass(field)"
          :data-field="field.key"
        >
          <dt
            class="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            <span class="truncate">{{ field.label }}</span>
            <LaisCopyButton
              v-if="field.type !== 'empty'"
              :text="String(field.text ?? '')"
              :aria-label="`Copiar ${field.label}`"
            />
          </dt>
          <dd class="mt-1 min-w-0 break-words text-lais-text">
            <a
              v-if="field.type === 'link'"
              :href="field.href"
              class="text-lais-primary underline decoration-dotted"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ field.text }}
            </a>
            <LaisStatusBadge
              v-else-if="field.type === 'boolean'"
              :label="field.text"
              :tone="field.raw ? 'positive' : 'neutral'"
              dot
            />
            <div v-else-if="field.type === 'list'" class="flex flex-wrap gap-1">
              <span
                v-for="(item, index) in field.items"
                :key="`${field.key}-${index}`"
                class="inline-flex rounded-full bg-lais-bg-soft px-2 py-0.5 text-xs"
              >
                {{ item }}
              </span>
            </div>
            <pre
              v-else-if="field.type === 'json'"
              class="max-h-56 overflow-auto rounded-lais-sm bg-lais-bg-soft p-2 font-mono text-xs leading-relaxed"
              >{{ field.text }}</pre
            >
            <span
              v-else
              :class="
                field.type === 'empty'
                  ? 'text-lais-text-muted'
                  : 'font-medium text-lais-text'
              "
            >
              {{ field.text }}
            </span>
          </dd>
        </div>
      </dl>
    </section>

    <p
      v-if="!preparedGroups.length"
      class="rounded-lais-sm border border-dashed border-lais-border-soft bg-lais-bg-soft p-4 text-center text-sm text-lais-text-muted"
    >
      Nenhum campo estruturado disponível para este item.
    </p>
  </div>
</template>
